/**
 * The one door to extension records (boards, tasks, documents, calendar,
 * projects, links) for the UI stores.
 *
 * Today every call goes to the desktop app's Rust `extension_records_*`
 * commands, exactly as the stores did before — same command, same argument
 * object, same result. The web app (web.apiboo.com) will install its own
 * backend with `setRecordsBackend` (IndexedDB cache + REST sync), so the stores
 * never need to know which one they are talking to.
 *
 * Argument objects are passed through untouched: `{ workspaceId, extensionId,
 * recordType, scopeId, record, recordId, … }` as the Rust commands define them.
 *
 * MCP handlers (src/mcp/**) deliberately keep calling `invoke` directly: MCP is
 * desktop-only and its attack-surface tests pin those exact calls.
 */
import { invoke } from '@tauri-apps/api/core';
import { noteRecordWrite, noteScopeWrite } from './syncSizeStore';
import { isReadOnlyCloudWrite, CLOUD_READ_ONLY } from '../plans/cloudAccess';

/** The desktop backend: a 1:1 pass-through to the Rust commands. */
export const desktopRecordsBackend = Object.freeze({
  list: (args) => invoke('extension_records_list', args),
  upsert: (args) => invoke('extension_records_upsert', args),
  casUpsert: (args) => invoke('extension_records_cas_upsert', args),
  delete: (args) => invoke('extension_records_delete', args),
  syncScope: (args) => invoke('extension_records_sync_scope', args),
  listMeta: (args) => invoke('extension_records_list_meta', args),
});

const METHODS = Object.keys(desktopRecordsBackend);
let backend = desktopRecordsBackend;

/**
 * Install another backend (the web app's). It must implement every method of
 * the desktop backend; `null` restores the desktop backend.
 */
export function setRecordsBackend(next) {
  if (next === null) {
    backend = desktopRecordsBackend;
    return;
  }
  const missing = METHODS.filter((m) => typeof next?.[m] !== 'function');
  if (missing.length) throw new Error(`records backend is missing: ${missing.join(', ')}`);
  backend = next;
}

/* Who wants to know that a record was written (git sync Stage 2: a connected
   scope's folder follows its documents / boards). Listeners never fail a write. */
const writeListeners = new Set();
export function onRecordsWritten(fn) {
  writeListeners.add(fn);
  return () => writeListeners.delete(fn);
}
function announceWrite(args) {
  for (const fn of writeListeners) {
    try {
      fn({ workspaceId: args?.workspaceId, extensionId: args?.extensionId });
    } catch {
      /* a listener's problem, not the write's */
    }
  }
}

/**
 * Plans v2 card 3.4: without `cloud_sync` the account's cloud boards and
 * documents are read-only (the server refuses the push too, and a refused push
 * would jam the scope's outbox). Refused HERE, before anything is written
 * locally or queued, for every write path at once.
 *
 * DELETING stays allowed (owner, 16.10.2026: GDPR erasure — a user may delete
 * their own read-only cloud items; backend #217 applies a batch's deletes and
 * rejects only its other ops): `deleteRecord`, and a `syncRecordScope` with
 * no records, which tombstones a whole board.
 */
function refuseReadOnlyCloud(args) {
  if (isReadOnlyCloudWrite(args)) throw Object.assign(new Error(CLOUD_READ_ONLY), { code: CLOUD_READ_ONLY });
}

export const listRecords = (args) => backend.list(args);
// The write's answer says whether its sync op was queued or refused as too
// large (syncSizeStore); it is still returned to the caller untouched.
export const upsertRecord = async (args) => {
  refuseReadOnlyCloud(args);
  const outcome = await backend.upsert(args);
  noteRecordWrite(args, outcome);
  announceWrite(args);
  return outcome;
};
export const casUpsertRecord = async (args) => {
  refuseReadOnlyCloud(args);
  const outcome = await backend.casUpsert(args);
  announceWrite(args);
  return outcome;
};
export const deleteRecord = async (args) => {
  const outcome = await backend.delete(args);
  announceWrite(args);
  return outcome;
};
export const syncRecordScope = async (args) => {
  // Empty = tombstone the whole scope (deleteBoard): a delete, so allowed.
  const deletesOnly = Array.isArray(args?.records) && args.records.length === 0;
  if (!deletesOnly) refuseReadOnlyCloud(args);
  const report = await backend.syncScope(args);
  noteScopeWrite(args, report);
  announceWrite(args);
  return report;
};
export const listRecordMeta = (args) => backend.listMeta(args);

/**
 * Several upserts and deletes of ONE extension in one go (a document's
 * changed blocks — docBlocks.js). A backend with `batch` (the web app's)
 * queues them as one push instead of one push-and-pull per record; otherwise
 * they are written one by one, exactly as upsertRecord / deleteRecord would.
 * `batch` is optional, so a backend without it stays valid.
 */
export const writeRecords = async ({ workspaceId, extensionId, upserts = [], deletes = [] }) => {
  if (!upserts.length && !deletes.length) return;
  if (upserts.length) refuseReadOnlyCloud({ workspaceId, extensionId, record: upserts[0] });
  if (typeof backend.batch === 'function') {
    const outcomes = (await backend.batch({ workspaceId, extensionId, upserts, deletes })) || [];
    upserts.forEach((record, i) => noteRecordWrite({ workspaceId, extensionId, record }, outcomes[i] ?? null));
  } else {
    for (const record of upserts) {
      const args = { workspaceId, extensionId, record };
      // eslint-disable-next-line no-await-in-loop
      noteRecordWrite(args, await backend.upsert(args));
    }
    for (const recordId of deletes) {
      // eslint-disable-next-line no-await-in-loop
      await backend.delete({ workspaceId, extensionId, recordId: String(recordId) });
    }
  }
  announceWrite({ workspaceId, extensionId });
};
